import { createElement, useState, useRef, useEffect } from 'react';
import { renderInlineCssVars, useDesignTokens } from '../../../theme';
import { useOnOutsidePopoverMouseDown } from '../../Popover/__internal__/hooks/useOnOutsidePopoverMouseDown';
import { Modal } from '../../Modal';
import { FloatingPanelProps, ClosingReason } from '../types';
import { FloatingPanelAnimation } from '../__internal__/types';

import cx from 'classnames';
import cn from './FloatingPanel.css.json';
import './FloatingPanel.css';

/**
 * Panel used in coordination with an ObjectSwitcher
 * @see https://rndconfluence.sas.com/confluence/pages/viewpage.action?spaceKey=NOVA&title=ObjectManager
 */
export const FloatingPanel = (props: FloatingPanelProps) => {
  const { onOpenComplete } = props;

  const [panel, setPanel] = useState<HTMLDivElement | null>(null);
  const [{ elementAcceptsAnimation, closing }, setAnimation] = useState<FloatingPanelAnimation>({
    elementAcceptsAnimation: false,
    closing: false,
  });

  const closingReason = useRef<ClosingReason>(null);
  const dt = useDesignTokens();
  const popperPaneTokens = dt.enableNovaTokens
    ? dt.backgroundsDepthNova.medium
    : dt.backgroundsDepth.medium;
  const cssVars = renderInlineCssVars(
    {
      ...popperPaneTokens,
      width: dt.spacing.size.l,
      bg: dt.backgroundsDepthNova.medium.bg,
      bd: dt.backgroundsDepthNova.medium.bd,
      'anim-dur-enter': dt.animation.duration.enter.l,
      'anim-tf-enter': dt.animation.timingFunction.enter.m,
      'anim-dur-exit': dt.animation.duration.exit.l,
      'anim-tf-exit': dt.animation.timingFunction.exit.m,
    },
    'floating-panel'
  );

  useOnOutsidePopoverMouseDown(() => {
    closingReason.current = 'blur';
    setAnimation((prev) => ({ ...prev, closing: true }));
  }, panel);

  const onClose = () => {
    setAnimation((prev) => ({ ...prev, closing: false }));
    props.onCloseComplete?.({
      closingReason: closingReason.current,
    });
  };

  // After the initial render call the onOpenComplete callback.
  useEffect(() => {
    if (panel) {
      onOpenComplete?.();
    }
  }, [onOpenComplete, panel]);

  // If animations are disabled run the onAnimationEnd event when closing is true
  if (!elementAcceptsAnimation && closing) {
    onClose();
  }

  return (
    <Modal isOpen={props.isOpen}>
      <div
        ref={setPanel}
        role={props.role}
        tabIndex={props.tabIndex ?? -1}
        aria-labelledby={props.ariaLabelledBy ? props.ariaLabelledBy : undefined}
        className={cx(cn.container, closing && cn.containerClosing)}
        style={cssVars}
        data-testid={props.testId}
        onAnimationEnd={({ currentTarget, target }) => {
          if (currentTarget === target) {
            if (closing) {
              onClose();
            } else {
              setAnimation((prev) => ({ ...prev, elementAcceptsAnimation: true }));
            }
          }
        }}
        onKeyDownCapture={({ key }) => {
          if (key === 'Escape') {
            closingReason.current = 'escape';
            setAnimation((prev) => ({ ...prev, closing: true }));
          }
        }}
      >
        {props.children}
      </div>
    </Modal>
  );
};
